import { Stack } from '@chakra-ui/react';
import { useCallback } from 'react';
import { Checkbox } from '~/components/Checkbox2';

export interface ICheckboxOption {
  friendlyValue: string;
  value: string;
}

export interface ICheckboxGroupOnChange extends ICheckboxOption {
  isChecked: boolean;
  optionsChecked: ICheckboxOption[];
}

export interface ICheckboxGroupProps extends ICheckboxOption {
  checked?: string[];
  enableTitleInOptions?: boolean;
  icon?: React.ReactElement;
  onChange: (checked: ICheckboxGroupOnChange) => void;
  options: ICheckboxOption[];
  trackId?: string;
}

export const CheckboxGroup: React.FC<ICheckboxGroupProps> = ({
  checked = [],
  enableTitleInOptions,
  friendlyValue,
  icon,
  onChange,
  options,
  value,
  trackId,
}) => {
  const hasAllChecked = checked.length === options.length;
  const isIndeterminate = Boolean(checked.length) && !hasAllChecked;

  const handleCheckAll = useCallback(() => {
    const optionsChecked = hasAllChecked ? [] : [...options];

    onChange({
      friendlyValue,
      isChecked: !hasAllChecked,
      optionsChecked,
      value,
    });
  }, [friendlyValue, hasAllChecked, onChange, options, value]);

  const handleCheckOption = useCallback(
    (channel: string) => {
      const updatedChecked = checked.includes(channel)
        ? [...checked].filter((v) => v !== channel)
        : [...checked, channel];
      const hasAllChecked = updatedChecked.length === options.length;
      const isIndeterminate = Boolean(updatedChecked.length) && !hasAllChecked;

      onChange({
        friendlyValue,
        isChecked: hasAllChecked || isIndeterminate,
        optionsChecked: options.filter(({ value }) =>
          updatedChecked.includes(value),
        ),
        value,
      });
    },
    [checked, friendlyValue, onChange, options, value],
  );

  return (
    <>
      <Checkbox
        isChecked={hasAllChecked}
        isIndeterminate={isIndeterminate}
        onChange={handleCheckAll}
        fontWeight={500}
        variant="right"
        trackId={trackId}
      >
        {icon} {friendlyValue}
      </Checkbox>

      {options.length && (
        <Stack width="100%" paddingLeft={6}>
          {options.map(({ friendlyValue, value }) => (
            <Checkbox
              isChecked={checked.includes(value)}
              onChange={() => handleCheckOption(value)}
              variant="right"
              textTransform="capitalize"
              key={value}
              trackId={trackId + '-' + value}
            >
              {enableTitleInOptions && friendlyValue} {friendlyValue}
            </Checkbox>
          ))}
        </Stack>
      )}
    </>
  );
};
